// The viewer document-selection dashboard (plan §4A) — "reading room".
// Grouped publisher → collection (single publisher collapses to collections),
// client-side filter past ~8 docs, single-doc auto-focus, friendly empty state.
// Opening a document targets the PORTAL /view/:id route in a NEW TAB — never
// the raw content URL (the viewer page holds the session and re-mints the
// single-use 90 s capability token).
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Search, ArrowUpRight, Inbox, RotateCw } from 'lucide-react';
import type { ViewerDocument } from '@ext-portal/shared';
import { apiFetch } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { AppShell } from '@/components/AppShell';
import { formatDate } from '@/lib/utils';

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; docs: ViewerDocument[] };

export function Dashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation('dashboard');
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [filter, setFilter] = useState('');

  async function fetchDocs() {
    setLoad({ status: 'loading' });
    try {
      const res = await apiFetch<{ data: ViewerDocument[] }>('/me/documents');
      setLoad({ status: 'ready', docs: res.data });
    } catch {
      setLoad({ status: 'error' });
    }
  }

  useEffect(() => {
    void fetchDocs();
  }, []);

  const docs = load.status === 'ready' ? load.docs : [];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.collections.some((c) => c.name.toLowerCase().includes(q)) ||
        d.publisherName.toLowerCase().includes(q),
    );
  }, [docs, filter]);

  // publisher → collection grouping driven by each doc's access PLACEMENTS
  // (the granted collections that contain it, each tagged with its owning
  // department). A doc shared across departments appears under each; the
  // '__ungrouped__' placement (direct grants) is named via t('ungrouped').
  const groups = useMemo(() => {
    const publishers = new Map<string, { name: string; collections: Map<string, { name: string; docs: ViewerDocument[] }> }>();
    for (const d of filtered) {
      for (const c of d.collections) {
        let pub = publishers.get(c.publisherSlug);
        if (!pub) {
          pub = { name: c.publisherName, collections: new Map() };
          publishers.set(c.publisherSlug, pub);
        }
        let col = pub.collections.get(c.slug);
        if (!col) {
          col = { name: c.slug === '__ungrouped__' ? t('ungrouped') : c.isDefault ? t('common:uncategorized') : c.name, docs: [] };
          pub.collections.set(c.slug, col);
        }
        col.docs.push(d);
      }
    }
    return publishers;
  }, [filtered, t]);

  const singleDoc = docs.length === 1 ? docs[0] : null;

  function openDoc(id: string) {
    // New tab on the portal origin (§4A) — the viewer route mints the token.
    window.open(`/view/${id}`, '_blank', 'noopener');
  }

  return (
    <AppShell>
      {/* Editorial header */}
      <div className="mb-10 animate-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-purple/70 mb-2">
          {t('greeting', { name: user?.displayName?.split(' ')[0] ?? '' })}
        </p>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium tracking-tight gradient-text pb-1">{t('title')}</h1>
            <p className="text-sm font-light text-slate-500 mt-1">{t('subtitle')}</p>
          </div>
          {docs.length > 8 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="paper-input !pl-9 w-72"
              />
            </div>
          )}
        </div>
      </div>

      {load.status === 'loading' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-white border border-slate-100 animate-pulse" />
          ))}
        </div>
      )}

      {load.status === 'error' && (
        <div className="text-center py-24 animate-fade-up">
          <p className="text-slate-500 mb-4">{t('errorLoad')}</p>
          <button type="button" onClick={() => void fetchDocs()} className="btn-quiet">
            <RotateCw className="w-4 h-4" />
            {t('retry')}
          </button>
        </div>
      )}

      {load.status === 'ready' && docs.length === 0 && (
        <div className="text-center py-24 animate-fade-up">
          <div className="inline-flex p-[2px] rounded-2xl bg-brand-gradient mb-5">
            <div className="p-4 rounded-2xl bg-white">
              <Inbox className="w-8 h-8 text-brand-purple" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-ink mb-1.5">{t('emptyTitle')}</h2>
          <p className="text-sm font-light text-slate-500 max-w-sm mx-auto">{t('emptyBody')}</p>
        </div>
      )}

      {load.status === 'ready' && docs.length > 0 && filtered.length === 0 && (
        <p className="text-center py-16 text-sm text-slate-500 animate-fade-up">{t('noMatches')}</p>
      )}

      {load.status === 'ready' &&
        [...groups.entries()].map(([pubSlug, pub]) => (
          <section key={pubSlug} className="mb-12">
            {[...pub.collections.entries()].map(([colSlug, col]) => (
              <div key={colSlug} className="mb-9">
                {/* Collection subheader + its owning publisher (provenance) */}
                <div className="flex items-center gap-4 mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-brand-purple shrink-0">
                    {col.name}
                    <span className="ml-2 font-medium normal-case tracking-normal text-slate-400">· {pub.name}</span>
                  </h3>
                  <div className="gradient-rule flex-1" />
                  <span className="text-[10px] font-semibold text-slate-400 tabular-nums">{col.docs.length}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
                  {col.docs.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => openDoc(d.id)}
                      autoFocus={singleDoc?.id === d.id}
                      className="doc-card group text-left rounded-xl bg-white border border-slate-200/80 p-5 pl-6 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 focus:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-rose/30 transition-all duration-300"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <FileText className="w-5 h-5 text-brand-purple/60 shrink-0 mt-0.5" />
                        <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-brand-rose group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                      </div>
                      <h4 className="mt-3 font-semibold text-[15px] text-ink leading-snug line-clamp-2">{d.title}</h4>
                      <p className="mt-2 text-xs font-light text-slate-400">
                        {t('updated', { date: formatDate(d.updatedAt, i18n.language) })}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </section>
        ))}
    </AppShell>
  );
}
